import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProfileSetupWizard } from '../../components/Profile';
import { ProfileSetupData } from '../../components/Profile/types';
import { useAuthContext } from '../../providers';
import { logger } from '../../utils/logger';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  PageContent,
  PageActions,
  ActionButton,
  BackButton,
  PageLoading,
  LoadingSpinner,
  LoadingText,
  PageError,
  ErrorMessage,
  ErrorAction
} from '../../styles/Profile/ProfilePages.styles';

export const ProfileSetup: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading, isAuthenticated } = useAuthContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const handleProfileComplete = async (profileData: ProfileSetupData) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Simulate API call to save profile
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      logger.log('Profile setup completed:', profileData);
      
      // Redirect to dashboard or profile preview
      navigate('/profile-preview', { 
        state: { profileData },
        replace: true 
      });
    } catch (error) {
      console.error('Failed to save profile:', error);
      setError('Failed to save profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    const confirmed = window.confirm(
      'Are you sure you want to cancel profile setup? Your progress will be lost.'
    );
    if (confirmed) {
      navigate('/dashboard');
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <PageContainer>
        <PageLoading>
          <LoadingSpinner />
          <LoadingText>Loading profile setup...</LoadingText>
        </PageLoading>
      </PageContainer>
    );
  }

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <PageError>
          <ErrorMessage>Please log in to access profile setup.</ErrorMessage>
          <ErrorAction onClick={() => navigate('/login')}>
            Go to Login
          </ErrorAction>
        </PageError>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>Profile Setup</PageTitle>
          <PageDescription>
            Create your profile to start connecting with others.
          </PageDescription>
        </PageHeader>
        
        <PageError>
          <ErrorMessage>{error}</ErrorMessage>
          <ErrorAction onClick={() => setError(null)}>
            Try Again
          </ErrorAction>
        </PageError>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Create Your Profile</PageTitle>
        <PageDescription>
          Let's set up your profile so others can discover the amazing person you are!
        </PageDescription>
      </PageHeader>

      <PageContent>
        <ProfileSetupWizard
          onComplete={handleProfileComplete}
          onCancel={handleCancel}
          initialData={{
            basicInfo: {
              firstName: user?.user_metadata?.full_name?.split(' ')[0] || '',
              lastName: user?.user_metadata?.full_name?.split(' ')[1] || '',
              dateOfBirth: new Date(),
              gender: 'other',
              location: {
                city: '',
                state: '',
                country: ''
              },
              occupation: '',
              education: ''
            },
            photos: [],
            bio: {
              content: '',
              characterCount: 0,
              maxCharacters: 500,
              lastUpdated: new Date()
            },
            interests: [],
            preferences: {
              ageRange: { min: 18, max: 65 },
              distance: 25,
              genderPreference: [],
              interestedIn: [],
              dealBreakers: []
            },
            currentStep: 1,
            isComplete: false,
            verificationStatus: 'pending'
          }}
        />
      </PageContent>

      <PageActions>
        <BackButton onClick={handleBack}>
          ← Back to Dashboard
        </BackButton>
      </PageActions>
    </PageContainer>
  );
};
